import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getBookingSettings, updateBookingSettings } from '@/services/bookingApi'
import type { BookingSettings as BookingSettingsType, ClientVerificationMethod } from '@/types'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Loader2, Check, Calendar, Clock, Phone } from 'lucide-react'
import { useSubscription } from '@/hooks/useSubscription'
import { HolidayNotificationBanner } from '@/components/dashboard/HolidayNotificationBanner'

interface BookingSettings extends BookingSettingsType {}

export default function BookingSettings() {
  const { t } = useTranslation('dashboard')
  const { hasFeature } = useSubscription()
  const hasClientVerification = hasFeature('booking.client_verification')
  const hasSmsNotifications = hasFeature('notifications.sms')
  const [settings, setSettings] = useState<BookingSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const settings = await getBookingSettings()
      setSettings(settings)
    } catch (err) {
      console.error('Failed to fetch booking settings:', err)
      setError('Failed to load booking settings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async () => {
    if (!settings) return

    setIsSaving(true)
    setError(null)
    try {
      await updateBookingSettings(settings)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (err) {
      console.error('Failed to save booking settings:', err)
      setError('Failed to save booking settings')
    } finally {
      setIsSaving(false)
    }
  }

  const handleNumberChange = (field: keyof BookingSettings, value: string) => {
    if (!settings) return
    const numValue = parseInt(value, 10)
    if (!isNaN(numValue) && numValue >= 0) {
      setSettings({ ...settings, [field]: numValue })
    }
  }

  const handleBooleanChange = (field: keyof BookingSettings, value: boolean) => {
    if (!settings) return
    const next: BookingSettings = { ...settings, [field]: value }
    if (field === 'require_owner_confirmation') {
      next.auto_confirm_after_verification = !value
    }
    if (field === 'auto_confirm_after_verification') {
      next.require_owner_confirmation = !value
    }
    setSettings(next)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error && !settings) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  if (!settings) {
    return (
      <div className="p-6 lg:p-8">
        <p className="text-gray-500">Settings not found</p>
      </div>
    )
  }

  return (
    <div className="p-6 lg:p-8 max-w-2xl">
      <HolidayNotificationBanner />

      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('bookings.settings.title')}</h1>
        <p className="text-gray-500">{t('bookings.settings.subtitle')}</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Advance Booking Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('bookings.settings.advanceBooking.title')}
          </CardTitle>
          <CardDescription>
            {t('bookings.settings.advanceBooking.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.advanceBooking.openDaysAhead')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.booking_open_days_ahead}
              onChange={(e) => handleNumberChange('booking_open_days_ahead', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.advanceBooking.openDaysAheadHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.advanceBooking.maxAdvanceDays')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.max_advance_days}
              onChange={(e) => handleNumberChange('max_advance_days', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.advanceBooking.maxAdvanceDaysHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Timing Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {t('bookings.settings.timing.title')}
          </CardTitle>
          <CardDescription>
            {t('bookings.settings.timing.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.timing.minNotice')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.min_booking_notice_minutes}
              onChange={(e) => handleNumberChange('min_booking_notice_minutes', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.timing.minNoticeHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.timing.buffer')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.buffer_minutes}
              onChange={(e) => handleNumberChange('buffer_minutes', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.timing.bufferHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.timing.postAppointmentGap')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.post_appointment_gap_minutes}
              onChange={(e) => handleNumberChange('post_appointment_gap_minutes', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.timing.postAppointmentGapHint')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('bookings.settings.timing.cancellationDeadline')}
            </label>
            <Input
              type="number"
              min="0"
              value={settings.cancellation_deadline_hours}
              onChange={(e) => handleNumberChange('cancellation_deadline_hours', e.target.value)}
            />
            <p className="text-sm text-gray-500 mt-1">
              {t('bookings.settings.timing.cancellationDeadlineHint')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Verification Settings */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            {t('bookings.settings.customerRequirements.title')}
          </CardTitle>
          <CardDescription>
            {t('bookings.settings.customerRequirements.description')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasClientVerification && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900">
              Automated email/SMS verification is available on paid plans. Free plans collect phone
              (required) and optional email; you confirm bookings manually in the schedule.
            </div>
          )}

          <div className={`flex items-center justify-between ${!hasClientVerification ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <p className="font-medium text-gray-700">Verification method</p>
              <p className="text-sm text-gray-500">
                How customers verify their booking after submitting details.
              </p>
            </div>
            <select
              value={settings.client_verification_method || 'email'}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  client_verification_method: e.target.value as ClientVerificationMethod,
                  requires_phone: e.target.value === 'phone' ? true : settings.requires_phone,
                })
              }
              className="rounded-lg border px-3 py-2 text-sm"
            >
              <option value="email">Email link</option>
              <option value="phone">Phone OTP</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Require owner confirmation</p>
              <p className="text-sm text-gray-500">
                Bookings stay pending until you manually confirm them after verification.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                handleBooleanChange(
                  'require_owner_confirmation',
                  !settings.require_owner_confirmation
                )
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.require_owner_confirmation ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.require_owner_confirmation ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Let customers choose staff</p>
              <p className="text-sm text-gray-500">
                When off, bookings are auto-assigned to the most available team member.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                handleBooleanChange(
                  'allow_customer_staff_selection',
                  !(settings.allow_customer_staff_selection ?? true)
                )
              }
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                (settings.allow_customer_staff_selection ?? true) ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  (settings.allow_customer_staff_selection ?? true) ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">{t('bookings.settings.customerRequirements.requirePhone')}</p>
              <p className="text-sm text-gray-500">
                {t('bookings.settings.customerRequirements.requirePhoneDesc')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleBooleanChange('requires_phone', !settings.requires_phone)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.requires_phone ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.requires_phone ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">{t('bookings.settings.customerRequirements.autoConfirm')}</p>
              <p className="text-sm text-gray-500">
                {t('bookings.settings.customerRequirements.autoConfirmDesc')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleBooleanChange('auto_confirm_after_verification', !settings.auto_confirm_after_verification)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.auto_confirm_after_verification ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.auto_confirm_after_verification ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Booking notifications</CardTitle>
          <CardDescription>
            Email notifications for new bookings are always sent. Configure optional SMS alerts below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between opacity-70">
            <div>
              <p className="font-medium text-gray-700">Email owner on new booking</p>
              <p className="text-sm text-gray-500">Always enabled</p>
            </div>
            <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded">On</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">Email assigned staff on new booking</p>
              <p className="text-sm text-gray-500">When a booking has assigned staff</p>
            </div>
            <button
              type="button"
              onClick={() => handleBooleanChange('notify_assigned_staff_on_new_booking', !settings.notify_assigned_staff_on_new_booking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_assigned_staff_on_new_booking !== false ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_assigned_staff_on_new_booking !== false ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">SMS owner on new booking</p>
              <p className="text-sm text-gray-500">
                {hasSmsNotifications ? 'Send SMS to business owner' : 'Upgrade to Growth+ for SMS notifications'}
              </p>
            </div>
            <button
              type="button"
              disabled={!hasSmsNotifications}
              onClick={() => handleBooleanChange('notify_owner_sms_on_new_booking', !settings.notify_owner_sms_on_new_booking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_owner_sms_on_new_booking ? 'bg-primary' : 'bg-gray-200'
              } ${!hasSmsNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_owner_sms_on_new_booking ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">SMS assigned staff on new booking</p>
              <p className="text-sm text-gray-500">
                {hasSmsNotifications ? 'Send SMS to assigned team member' : 'Upgrade to Growth+ for SMS notifications'}
              </p>
            </div>
            <button
              type="button"
              disabled={!hasSmsNotifications}
              onClick={() => handleBooleanChange('notify_assigned_staff_sms_on_new_booking', !settings.notify_assigned_staff_sms_on_new_booking)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.notify_assigned_staff_sms_on_new_booking ? 'bg-primary' : 'bg-gray-200'
              } ${!hasSmsNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.notify_assigned_staff_sms_on_new_booking ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Booking widget preview</CardTitle>
          <CardDescription>
            Open your public booking page to preview the customer flow for this location.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <WidgetPreviewLink />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('appearance.save.saving')}
            </>
          ) : saved ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              {t('appearance.save.saved')}
            </>
          ) : (
            t('appearance.save.saveChanges')
          )}
        </Button>
      </div>
    </div>
  )
}

function WidgetPreviewLink() {
  const [href, setHref] = useState<string | null>(null)
  const [embedHref, setEmbedHref] = useState<string | null>(null)
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const api = (await import('@/lib/api-client')).default
        const res = await api.get('/user/profiles')
        const profiles = res.data?.data ?? []
        const profileId = localStorage.getItem('current_profile_id')
        const current =
          profiles.find((p: { id: string }) => p.id === profileId) ?? profiles[0]
        if (!cancelled && current?.slug) {
          setHref(`/book/${current.slug}`)
          setEmbedHref(`${origin}/embed/book/${current.slug}`)
        }
      } catch {
        // ignore preview load errors
      }
    })()
    return () => {
      cancelled = true
    }
  }, [origin])

  if (!href) {
    return <p className="text-sm text-muted-foreground">Loading preview link…</p>
  }

  const iframeSnippet = embedHref
    ? `<iframe src="${embedHref}" title="Book appointment" width="100%" height="720" style="border:0;border-radius:12px" allow="clipboard-write"></iframe>`
    : ''

  return (
    <div className="space-y-4">
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-primary hover:underline text-sm font-medium"
      >
        Open booking page
      </a>
      {iframeSnippet && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">Embed on your website</p>
          <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all">
            {iframeSnippet}
          </pre>
        </div>
      )}
    </div>
  )
}
